import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  useApolloClient,
  from,
  TypePolicies,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useEffect, useState } from 'react';
import useRefreshAuth from 'shared/modules/auth/hooks/use-refresh-auth';
import useSignOutUser from 'web/modules/auth/hooks/use-sign-out-user';
import eventBus from 'shared/utils/event-bus';

/**
 * We disable normalisation for Charges since the same Charge ID
 * can have different attributes in different contexts. For example, a single
 * Charge ID can have a different "amount" value in consumerUsageSummary's
 * "monthlyChargesBreakdown" and "recentSettlements". Normalising Charges
 * would currently lead to bugs (e.g., the incorrect Charge amount being displayed.).
 * This modelling should idealy be improved/fixed server-side, but for now this
 * is the way it is.
 *
 * We disable normalisation for Installments since Installments have no ID
 * in the consumerUsageSummary response payload. Having no ID causes issues for
 * the Apollo cache.
 *
 * SOURCE: https://www.apollographql.com/docs/react/caching/cache-configuration/#disabling-normalization
 */
const typePolicies: TypePolicies = {
  Charge: {
    keyFields: false,
  },
  Installment: {
    keyFields: false,
  },
};

const client = new ApolloClient({
  cache: new InMemoryCache({ typePolicies }),
});

const useCustomFetch = () => {
  const refreshAuth = useRefreshAuth();
  const signOutUser = useSignOutUser();

  /**
   * A custom Fetch function that fetches a new accessToken if it expires
   */
  const customFetch = (uri: string, options: any) => {
    // We don't want to intercept RefreshAuth mutation
    // or else this becomes an endless loop
    if (JSON.parse(options.body).operationName === 'RefreshAuth') {
      return fetch(uri, options);
    }

    return new Promise<any>(async (resolve, reject) => {
      // Save the original fetch function
      const originalFetch = () => fetch(uri, options);

      try {
        // Check if the fetch still works
        let originalFetchResult = await originalFetch();

        // TODO: Handle ReCaptcha

        // Fetch does not treat 4xx and 5xx as errors. These are normal backend responses to Fetch's perspective.
        // Fetch only recognizes an error when the call does not resolve. That is, the backend is basically crashing down.
        if (originalFetchResult.ok && originalFetchResult.status < 400) {
          resolve(originalFetchResult);
          return;
        }

        // If the backend returns 401, then the accessToken must have expired. We need to fetch a new one.
        if (originalFetchResult.status === 401) {
          try {
            await refreshAuth();
            // Then perform the original fetch again
            originalFetchResult = await originalFetch();
            // It should succeed at this point
            resolve(originalFetchResult);
          } catch (err) {
            // If the refreshToken also fails, then sign out
            reject(err);

            signOutUser(
              // Sign the user out without calling the signOut mutation
              // (this is to prevent possible infinite loops)
              false,
              // If the query that fails is the SignOut query, there is no "reSignIn" intention here, because
              // the consumer explicitly requested for the sign-out. Therefore, there is no need to pass for the
              // `pathName` here.
              JSON.parse(options.body).operationName === 'SignOut'
                ? ''
                : window.location.pathname
            );
          }
          return;
        }

        // If all else fails, then just return the error.
        reject(originalFetchResult);
      } catch (err) {
        // If it failed, then we may need to get a new access token
        reject(err);
      }
    });
  };

  return customFetch;
};

const useCustomApolloLink = () => {
  const customFetch = useCustomFetch();
  const apolloClient = useApolloClient();
  // This sessionToken is only for Impersonation
  const [sessionToken, setSessionToken] = useState('');

  // We want setLink to be run only once in the entire app lifecycle
  useEffect(() => {
    const httpLink = createHttpLink({
      // Most queries uses the private endpoint
      // So we don't have to specify the URI for each query except for public ones
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PRIVATE,
      credentials: 'include',
      fetch: customFetch,
    });

    const authLink = setContext(() => {
      return {
        headers: {
          ...(sessionToken && {
            Authorization: `Bearer ${sessionToken}`,
          }),
          'Device-Type': 'WEB',
          'App-Version': process.env.APP_VERSION,
        },
      };
    });

    apolloClient.setLink(from([authLink, httpLink]));
  }, [sessionToken]);

  // I think this is better done with React.Context
  // This is only used for impersonating an account. This token is ephemeral, and should NOT persist across refreshes.
  useEffect(() => {
    eventBus.on('authToken:set', (token: string) => setSessionToken(token));
  }, []);
};

export default client;
export { ApolloProvider, useCustomApolloLink, useCustomFetch };
